import React from 'react';
import type { Quadrant, Ring, Entry } from '../../utils/types';

import RadarGrid from '../RadarGrid';
import RadarEntry from '../RadarEntry';
import RadarBubble from '../RadarBubble';
import RadarFooter from '../RadarFooter';
import RadarLegend from '../RadarLegend';

export type Props = {
  width: number;
  height: number;
  radius: number;
  rings: Ring[];
  quadrants: Quadrant[];
  entries: Entry[];
  activeEntry?: Entry;
  onEntryMouseEnter?: (entry: Entry) => void;
  onEntryMouseLeave?: (entry: Entry) => void;
};

// A component that draws the radar circle.
const RadarPlot = (props: Props): JSX.Element => {
  const {
    width,
    height,
    radius,
    quadrants,
    rings,
    entries,
    activeEntry,
    onEntryMouseEnter,
    onEntryMouseLeave,
  } = props;

  return (
    <g data-testid="radar-plot">
      <RadarLegend
        quadrants={quadrants}
        rings={rings}
        entries={entries}
        onEntryMouseEnter={
          onEntryMouseEnter && (entry => onEntryMouseEnter(entry))
        }
        onEntryMouseLeave={
          onEntryMouseLeave && (entry => onEntryMouseLeave(entry))
        }
      />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        <RadarGrid radius={radius} rings={rings} />
        <RadarFooter
          x={-0.5 * width}
          y={0.5 * height}
          spaces={rings.some(ring => ring.description) ? 2 : undefined}
          labels={
            rings
              .map(
                ring => ring.description && `${ring.name}: ${ring.description}`,
              )
              .filter(e => e) as string[]
          }
        />
        {entries.map(entry => (
          <RadarEntry
            key={entry.id}
            x={entry.x || 0}
            y={entry.y || 0}
            color={entry.color || ''}
            entryId={entry.id}
            value={(entry?.index || 0) + 1}
            url={entry.url}
            links={entry.links}
            description={entry.description}
            moved={entry.moved}
            title={entry.title}
            timeline={entry.timeline}
            onMouseEnter={onEntryMouseEnter && (() => onEntryMouseEnter(entry))}
            onMouseLeave={onEntryMouseLeave && (() => onEntryMouseLeave(entry))}
          />
        ))}
        <RadarBubble
          visible={!!activeEntry}
          text={activeEntry?.title || ''}
          x={activeEntry?.x || 0}
          y={activeEntry?.y || 0}
        />
      </g>
    </g>
  );
};

export default RadarPlot;